import { getClientSession } from "@/services/auth/clientSession";
import { API_ENDPOINTS } from "@/config/api";
import {
  MONITORING_LOG_TIMEOUT_MS,
  buildSystemLogPayload,
  fetchMappedSystemLogs,
  isCriticalLogLevel,
  shouldGateNonCriticalAdminLogging,
  shouldRetryMonitoringAfterOutage,
} from "./microserviceHelpers";

export interface SystemLogRequest {
  level: "debug" | "info" | "warn" | "error";
  component: string;
  message: string;
  service?: string;
  user_id?: string;
  metadata?: Record<string, unknown>;
}

export interface PerformanceMetric {
  metric_name: string;
  metric_value: number;
  service_name: string;
  timestamp?: string;
  metadata?: Record<string, unknown>;
}

export interface SystemLogsResponse {
  success: boolean;
  data: Array<{
    id: string;
    level: string;
    component: string;
    message: string;
    service?: string;
    user_id?: string;
    metadata?: Record<string, unknown>;
    timestamp: string;
    created_at: string;
  }>;
}

export interface PerformanceMetricsResponse {
  success: boolean;
  data: Array<{
    id: string;
    metric_name: string;
    metric_value: number;
    service_name: string;
    timestamp: string;
    created_at: string;
  }>;
}

type HealthCheckStatus = "ok" | "error" | "unknown";

export interface HealthCheckResponse {
  status: "healthy" | "degraded" | "unhealthy";
  timestamp: string;
  service: string;
  checks: {
    database: HealthCheckStatus;
    memory: HealthCheckStatus;
    uptime: HealthCheckStatus;
  };
}

/**
 * Monitoring Microservice Client
 * Handles all monitoring, logging, and performance tracking operations
 * Now uses system-service API instead of Supabase Edge Functions
 */
class MonitoringMicroservice {
  private serviceAvailable: boolean | null = null;
  private lastHealthCheck: number = 0;
  private readonly HEALTH_CHECK_INTERVAL = 60000; // Check every minute

  /**
   * Check if system-service is available (cached check)
   */
  private shouldAttemptLogging(): boolean {
    if (this.serviceAvailable !== false) {
      return true;
    }

    if (shouldRetryMonitoringAfterOutage(this.serviceAvailable, this.lastHealthCheck, Date.now())) {
      this.serviceAvailable = null;
      return true;
    }

    return false;
  }

  /**
   * Log a system event
   * Uses system-service API endpoint: POST /api/system/logs
   * CRITICAL: Only logs for admin/super admin users (except critical errors)
   */
  async logSystemEvent(logData: SystemLogRequest): Promise<{ success: boolean }> {
    try {
      // Skip logging if service is known to be unavailable
      if (!this.shouldAttemptLogging()) {
        return { success: false };
      }

      // Get current user info if available
      let userId: string | undefined;
      let userEmail: string | undefined;

      try {
        const { data: sessionData } = await getClientSession();
        if (sessionData?.session?.user) {
          userId = sessionData.session.user.id;
          userEmail = sessionData.session.user.email;
        }
      } catch (err) {
        console.error("Operation failed:", err);
        // Non-critical - continue without user info
      }

      // CRITICAL: Only log system events for admin/super admin users
      // Exception: Always log critical errors (level === "error" or "fatal") for debugging
      const isCriticalError = isCriticalLogLevel(logData.level);
      if (shouldGateNonCriticalAdminLogging(isCriticalError, userId)) {
        // For non-critical events, gate by admin status
        try {
          const { isAdminOrSuperAdmin } = await import("@/utils/adminCheck");
          const isAdmin = await isAdminOrSuperAdmin();
          if (!isAdmin) {
            // Silently skip logging for non-admin users (except critical errors)
            return { success: false };
          }
        } catch (err) {
          console.error("Operation failed:", err);
          // Fail-safe: skip logging if admin check fails
          return { success: false };
        }
      }

      // Map to system-service format
      const payload = buildSystemLogPayload(logData, userId, userEmail);

      // Use system-service API endpoint (fire-and-forget, non-blocking)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), MONITORING_LOG_TIMEOUT_MS);

      // Fire-and-forget fetch with error tracking
      fetch(API_ENDPOINTS.SYSTEM.LOGS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(userId
            ? {
                Authorization: `Bearer ${(await getClientSession()).data.session?.access_token || ""}`,
              }
            : {}),
        },
        credentials: "include",
        body: JSON.stringify(payload),
        signal: controller.signal,
      })
        .then((response) => {
          clearTimeout(timeoutId);
          if (response.ok) {
            this.serviceAvailable = true;
            this.lastHealthCheck = Date.now();
          } else {
            // Service returned error, mark as unavailable
            this.serviceAvailable = false;
            this.lastHealthCheck = Date.now();
          }
        })
        .catch(() => {
          clearTimeout(timeoutId);
          // Network error or timeout - mark service as unavailable
          this.serviceAvailable = false;
          this.lastHealthCheck = Date.now();
        });

      // Always return success immediately (fire-and-forget pattern)
      return { success: true };
    } catch (error) {
      console.error("Operation failed:", error);
      // Silently ignore errors - logging is non-critical
      return { success: false };
    }
  }

  /**
   * Record a performance metric
   * Uses system-service API endpoint: POST /api/system/metrics
   * CRITICAL: Only records metrics for admin/super admin users
   */
  async recordPerformanceMetric(metric: PerformanceMetric): Promise<{ success: boolean }> {
    try {
      // Get current user info if available
      let userId: string | undefined;

      try {
        const { data: sessionData } = await getClientSession();
        if (sessionData?.session?.user) {
          userId = sessionData.session.user.id;
        }
      } catch (err) {
        console.error("Operation failed:", err);
        // Non-critical - continue without user info
      }

      // CRITICAL: Only record metrics for admin/super admin users
      if (userId) {
        try {
          const { isAdminOrSuperAdmin } = await import("@/utils/adminCheck");
          const isAdmin = await isAdminOrSuperAdmin();
          if (!isAdmin) {
            // Silently skip metric recording for non-admin users
            return { success: false };
          }
        } catch (err) {
          console.error("Operation failed:", err);
          // Fail-safe: skip recording if admin check fails
          return { success: false };
        }
      }

      // Map to system-service format
      const payload = {
        serviceName: metric.service_name || "frontend",
        metricName: metric.metric_name || "unknown",
        cpu: 0,
        memoryUsed: 0,
        memoryTotal: 0,
        apiResponseTime: metric.metric_value || 0,
        requestsPerMinute: 0,
        activeConnections: 0,
        databaseConnections: 0,
        databaseQueryTime: 0,
        cacheHitRatio: 0,
        webhookResponseTime: 0,
        queueLength: 0,
        errorRate: 0,
      };

      // Use system-service API endpoint (fire-and-forget, non-blocking)
      fetch(API_ENDPOINTS.SYSTEM.METRICS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(userId
            ? {
                Authorization: `Bearer ${(await getClientSession()).data.session?.access_token || ""}`,
              }
            : {}),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      }).catch(() => {
        // Silently ignore errors - logging is non-critical
      });

      // Always return success immediately (fire-and-forget pattern)
      return { success: true };
    } catch (error) {
      console.error("Operation failed:", error);
      // Silently ignore errors - logging is non-critical
      return { success: false };
    }
  }

  /**
   * Get system logs
   * Uses system-service API endpoint: GET /api/system/logs
   */
  async getSystemLogs(
    options: {
      limit?: number;
      level?: string;
      component?: string;
    } = {}
  ): Promise<SystemLogsResponse> {
    try {
      const { data: sessionData } = await getClientSession();
      if (!sessionData.session) {
        throw new Error("No active session");
      }

      return fetchMappedSystemLogs({
        logsUrl: API_ENDPOINTS.SYSTEM.LOGS,
        accessToken: sessionData.session.access_token,
        query: options,
      });
    } catch (error) {
      console.warn("Error fetching system logs:", error);
      return { success: false, data: [] };
    }
  }

  /**
   * Get performance metrics
   * Uses system-service API endpoint: GET /api/system/metrics
   */
  async getPerformanceMetrics(
    options: {
      limit?: number;
      timeRange?: "1h" | "24h" | "7d" | "30d";
    } = {}
  ): Promise<PerformanceMetricsResponse> {
    try {
      const { data: sessionData } = await getClientSession();
      if (!sessionData.session) {
        throw new Error("No active session");
      }

      const params = new URLSearchParams();
      if (options.limit) params.append("limit", options.limit.toString());
      if (options.timeRange) params.append("timeRange", options.timeRange);

      const response = await fetch(`${API_ENDPOINTS.SYSTEM.METRICS}?${params.toString()}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      const result = await response.json();

      if (!response.ok) {
        console.warn("Failed to fetch performance metrics:", result);
        return { success: false, data: [] };
      }

      // Map system-service response format to expected format
      const metrics = Array.isArray(result) ? result : result.data || [];
      return {
        success: true,
        data: metrics.map((metric: Record<string, unknown>) => ({
          id: metric.id || `metric-${Date.now()}`,
          metric_name: metric.metric_name || "unknown",
          metric_value: metric.metric_value || metric.api_response_time || 0,
          service_name: metric.service_name || "unknown",
          timestamp: metric.timestamp || metric.created_at || new Date().toISOString(),
          created_at: metric.created_at || metric.timestamp || new Date().toISOString(),
        })),
      };
    } catch (error) {
      console.warn("Error fetching performance metrics:", error);
      return { success: false, data: [] };
    }
  }

  /**
   * Health check
   * Uses system-service API endpoint: GET /api/system/health
   */
  async healthCheck(): Promise<HealthCheckResponse> {
    try {
      const response = await fetch(`${API_ENDPOINTS.SYSTEM.LOGS.replace("/logs", "/health")}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (response.ok) {
        return {
          status: "healthy",
          timestamp: new Date().toISOString(),
          service: "system-service",
          checks: {
            database: "ok",
            memory: "ok",
            uptime: "ok",
          },
        };
      }

      return {
        status: "degraded",
        timestamp: new Date().toISOString(),
        service: "system-service",
        checks: {
          database: "unknown",
          memory: "unknown",
          uptime: "unknown",
        },
      };
    } catch (error) {
      console.error("Operation failed:", error);
      // Health check failed - service is unavailable
      return {
        status: "unhealthy",
        timestamp: new Date().toISOString(),
        service: "system-service",
        checks: {
          database: "unknown",
          memory: "unknown",
          uptime: "unknown",
        },
      };
    }
  }
}

export const monitoringMicroservice = new MonitoringMicroservice();
